// Admin CRUD for the driver vehicle-check checklist -- replaces what used
// to be a hardcoded list in VehicleInspection.tsx. Same shape as
// RosterPositionsManagement.tsx: load all rows, inline edit, add-new form,
// delete with confirm. Category is free text (not a fixed enum) so a brand
// new category can just be typed in.
import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "@/components/ui/use-toast";
import { supabase } from "@/lib/supabase";
import { Plus, Save, X, Edit, Trash2, ClipboardList } from "lucide-react";
import { loadVehicleTypes, type VehicleTypeRow } from "@/lib/vehicleTypes";
import {
  loadCheckItemTypeRestrictions,
  setCheckItemTypeRestrictions,
  type CheckItemTypeRestrictions,
} from "@/lib/vehicleCheckItems";

interface CheckItem {
  id: string;
  category: string;
  item_key: string;
  label: string;
  sort_order: number;
  is_active: boolean;
}

const emptyNewForm = { category: "", item_key: "", label: "", sort_order: 100, is_active: true };

const VehicleCheckItemsSettings: React.FC = () => {
  const [items, setItems] = useState<CheckItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<Partial<CheckItem>>({});
  const [showAddForm, setShowAddForm] = useState(false);
  const [newForm, setNewForm] = useState(emptyNewForm);

  // Which vehicle types each item is scoped to -- an item with no rows here
  // applies to every type (unchanged default behaviour). See
  // src/lib/vehicleCheckItems.ts and VehicleInspection.tsx's driver-side filter.
  const [vehicleTypes, setVehicleTypes] = useState<VehicleTypeRow[]>([]);
  const [restrictions, setRestrictions] = useState<CheckItemTypeRestrictions>(new Map());
  const [togglingRestriction, setTogglingRestriction] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("vehicle_check_items")
      .select("*")
      .order("sort_order", { ascending: true });

    if (error) {
      toast({ title: "Error", description: "Failed to load check items: " + error.message, variant: "destructive" });
    } else {
      setItems(data || []);
    }
    setLoading(false);
  };

  const loadRestrictions = async () => {
    setRestrictions(await loadCheckItemTypeRestrictions());
  };

  useEffect(() => {
    load();
    loadRestrictions();
    loadVehicleTypes().then(setVehicleTypes);
  }, []);

  const toggleItemVehicleType = async (item: CheckItem, vehicleType: string) => {
    const current = restrictions.get(item.item_key) ?? new Set<string>();
    const next = new Set(current);
    if (next.has(vehicleType)) next.delete(vehicleType);
    else next.add(vehicleType);

    setTogglingRestriction(item.item_key);
    try {
      await setCheckItemTypeRestrictions(item.item_key, Array.from(next));
      setRestrictions((prev) => {
        const updated = new Map(prev);
        updated.set(item.item_key, next);
        return updated;
      });
    } catch (err) {
      toast({
        title: "Error",
        description: "Failed to update vehicle types: " + (err instanceof Error ? err.message : String(err)),
        variant: "destructive",
      });
    } finally {
      setTogglingRestriction(null);
    }
  };

  const startEdit = (item: CheckItem) => {
    setEditingId(item.id);
    setEditForm({ ...item });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({});
  };

  const saveEdit = async () => {
    if (!editingId || !editForm.label?.trim() || !editForm.category?.trim()) {
      toast({ title: "Error", description: "Category and label are required", variant: "destructive" });
      return;
    }
    setSaving(true);
    const { error } = await supabase
      .from("vehicle_check_items")
      .update({
        category: editForm.category.trim(),
        label: editForm.label.trim(),
        sort_order: editForm.sort_order ?? 100,
        is_active: editForm.is_active ?? true,
      })
      .eq("id", editingId);

    if (error) {
      toast({ title: "Error", description: "Failed to update: " + error.message, variant: "destructive" });
    } else {
      toast({ title: "Saved", description: `"${editForm.label}" updated` });
      setEditingId(null);
      setEditForm({});
      load();
    }
    setSaving(false);
  };

  const addItem = async () => {
    const category = newForm.category.trim();
    const label = newForm.label.trim();
    const keySource = newForm.item_key.trim() || label;
    const item_key = keySource
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "");

    if (!category || !label || !item_key) {
      toast({ title: "Error", description: "Category and label are required", variant: "destructive" });
      return;
    }
    if (items.some((i) => i.item_key === item_key)) {
      toast({ title: "Error", description: "An item with this key already exists", variant: "destructive" });
      return;
    }

    setSaving(true);
    const { error } = await supabase.from("vehicle_check_items").insert({
      category,
      item_key,
      label,
      sort_order: newForm.sort_order,
      is_active: newForm.is_active,
    });

    if (error) {
      toast({ title: "Error", description: "Failed to add: " + error.message, variant: "destructive" });
    } else {
      toast({ title: "Added", description: `"${label}" added to the checklist` });
      setShowAddForm(false);
      setNewForm(emptyNewForm);
      load();
    }
    setSaving(false);
  };

  const deleteItem = async (item: CheckItem) => {
    if (!confirm(`Delete "${item.label}"? This only removes it from future checks -- past submitted results are unaffected.`)) return;
    const { error } = await supabase.from("vehicle_check_items").delete().eq("id", item.id);
    if (error) {
      toast({ title: "Error", description: "Failed to delete: " + error.message, variant: "destructive" });
    } else {
      toast({ title: "Deleted", description: `"${item.label}" removed` });
      load();
    }
  };

  const categories = Array.from(new Set(items.map((i) => i.category)));

  return (
    <div className="rounded-xl border border-yellow-900/20 bg-black/20 p-4 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h4 className="font-medium text-foreground flex items-center gap-2">
            <ClipboardList className="h-4 w-4 text-yellow-500" />
            Vehicle Check Items
          </h4>
          <p className="text-xs text-muted-foreground mt-1">
            What shows up in a driver's Vehicle Check checklist. Turn items off instead of deleting them if you
            might want them back -- deleting only affects future checks, past results are never touched. Use
            "Applies to" on each item to scope it to specific vehicle types (e.g. a hoist photo only on wheelchair
            vans) -- leave none selected to keep it on every vehicle.
          </p>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={() => setShowAddForm((s) => !s)}
          className="border-yellow-600 text-yellow-400 hover:bg-yellow-900"
        >
          <Plus className="w-4 h-4 mr-1" />
          Add Item
        </Button>
      </div>

      {showAddForm && (
        <div className="space-y-3 p-3 rounded-lg border border-gray-600 bg-gray-800/50">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <Label className="text-yellow-500 text-xs">Category</Label>
              <Input
                value={newForm.category}
                onChange={(e) => setNewForm((f) => ({ ...f, category: e.target.value }))}
                placeholder="e.g. Fluids"
                list="vehicle-check-categories"
              />
              <datalist id="vehicle-check-categories">
                {categories.map((c) => (
                  <option key={c} value={c} />
                ))}
              </datalist>
            </div>
            <div className="md:col-span-2">
              <Label className="text-yellow-500 text-xs">Label (what the driver sees)</Label>
              <Input
                value={newForm.label}
                onChange={(e) => setNewForm((f) => ({ ...f, label: e.target.value }))}
                placeholder="e.g. Coolant level"
              />
            </div>
          </div>
          <div className="flex items-end gap-3">
            <div className="w-32">
              <Label className="text-yellow-500 text-xs">Sort Order</Label>
              <Input
                type="number"
                value={newForm.sort_order}
                onChange={(e) => setNewForm((f) => ({ ...f, sort_order: parseInt(e.target.value, 10) || 100 }))}
              />
            </div>
            <div className="flex items-center gap-2 pb-2">
              <Checkbox
                checked={newForm.is_active}
                onCheckedChange={(v) => setNewForm((f) => ({ ...f, is_active: !!v }))}
              />
              <Label className="text-sm">Active</Label>
            </div>
            <div className="flex-1" />
            <Button size="sm" variant="ghost" onClick={() => { setShowAddForm(false); setNewForm(emptyNewForm); }}>
              <X className="w-4 h-4 mr-1" />
              Cancel
            </Button>
            <Button size="sm" onClick={addItem} disabled={saving} className="golden-button">
              <Save className="w-4 h-4 mr-1" />
              Add
            </Button>
          </div>
        </div>
      )}

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading...</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-muted-foreground">No check items yet.</p>
      ) : (
        <div className="space-y-2">
          {items.map((item) =>
            editingId === item.id ? (
              <div key={item.id} className="space-y-3 p-3 rounded-lg border border-gray-600 bg-gray-800/50">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <Label className="text-yellow-500 text-xs">Category</Label>
                    <Input
                      value={editForm.category || ""}
                      onChange={(e) => setEditForm((f) => ({ ...f, category: e.target.value }))}
                      list="vehicle-check-categories"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-yellow-500 text-xs">Label</Label>
                    <Input
                      value={editForm.label || ""}
                      onChange={(e) => setEditForm((f) => ({ ...f, label: e.target.value }))}
                    />
                  </div>
                </div>
                <div className="flex items-end gap-3">
                  <div className="w-32">
                    <Label className="text-yellow-500 text-xs">Sort Order</Label>
                    <Input
                      type="number"
                      value={editForm.sort_order ?? 100}
                      onChange={(e) => setEditForm((f) => ({ ...f, sort_order: parseInt(e.target.value, 10) || 100 }))}
                    />
                  </div>
                  <div className="flex items-center gap-2 pb-2">
                    <Checkbox
                      checked={editForm.is_active ?? true}
                      onCheckedChange={(v) => setEditForm((f) => ({ ...f, is_active: !!v }))}
                    />
                    <Label className="text-sm">Active</Label>
                  </div>
                  <div className="flex-1" />
                  <Button size="sm" variant="ghost" onClick={cancelEdit}>
                    <X className="w-4 h-4 mr-1" />
                    Cancel
                  </Button>
                  <Button size="sm" onClick={saveEdit} disabled={saving} className="golden-button">
                    <Save className="w-4 h-4 mr-1" />
                    Save
                  </Button>
                </div>
              </div>
            ) : (
              <div
                key={item.id}
                className={`p-3 rounded-lg border border-gray-700 space-y-2 ${
                  item.is_active ? "bg-gray-800/30" : "bg-gray-900/40 opacity-60"
                }`}
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-xs uppercase tracking-wide text-yellow-500/80">{item.category}</span>
                      {!item.is_active && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-700 text-gray-300">Inactive</span>
                      )}
                    </div>
                    <p className="text-sm text-foreground truncate">{item.label}</p>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button size="sm" variant="ghost" onClick={() => startEdit(item)}>
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => deleteItem(item)} className="text-red-400 hover:text-red-300">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {vehicleTypes.length > 0 && (
                  <div className="flex flex-wrap items-center gap-1.5 pt-1 border-t border-gray-700/60">
                    <span className="text-[10px] text-muted-foreground uppercase tracking-wide mr-1">Applies to</span>
                    {vehicleTypes.map((vt) => {
                      const restricted = restrictions.get(item.item_key);
                      const isChecked = !!restricted?.has(vt.vehicle_type);
                      return (
                        <button
                          key={vt.vehicle_type}
                          type="button"
                          disabled={togglingRestriction === item.item_key}
                          onClick={() => toggleItemVehicleType(item, vt.vehicle_type)}
                          className={`px-2 py-0.5 rounded text-[11px] font-medium border transition-colors ${
                            isChecked
                              ? "bg-yellow-900/30 text-yellow-400 border-yellow-600/60"
                              : "bg-transparent text-muted-foreground border-gray-600 hover:border-yellow-600/40"
                          }`}
                        >
                          {vt.vehicle_type}
                        </button>
                      );
                    })}
                    {!restrictions.get(item.item_key)?.size && (
                      <span className="text-[10px] text-muted-foreground italic ml-1">
                        (none selected = every vehicle type)
                      </span>
                    )}
                  </div>
                )}
              </div>
            )
          )}
        </div>
      )}
    </div>
  );
};

export default VehicleCheckItemsSettings;
